import { apiClient } from './request';
import { create, fromJson, type JsonValue, toJson } from '@bufbuild/protobuf';
import { type MCP, MCPSchema, type MCPTool } from '@/proto/agentplatform/v1/mcp_pb';
import {
  ListMCPsResponseSchema,
  CreateMCPRequestSchema,
  CreateMCPResponseSchema,
  GetMCPRequestSchema,
  GetMCPResponseSchema,
  UpdateMCPRequestSchema,
  UpdateMCPResponseSchema,
  DeleteMCPRequestSchema,
  DeleteMCPResponseSchema,
  ListMCPToolsRequestSchema,
  ListMCPToolsResponseSchema,
  TestMCPConnectionRequestSchema,
  TestMCPConnectionResponseSchema,
  TestMCPConnectionDataSchema,
  type TestMCPConnectionData,
} from '@/proto/agentplatform/httpv2/mcp_pb';
import { type ListRequest, ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';

/** 列表返回的通用形状，兼容页面既有的 `data.data` / `data.total` 读法。 */
export interface MCPListResult {
  data: MCP[];
  total: number;
}

export interface MCPToolListResult {
  data: MCPTool[];
}

export const mcpApi = {
  listMCPs: async (params: ListRequest): Promise<MCPListResult> => {
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ListMCPs',
      toJson(ListRequestSchema, params)
    );
    const resp = fromJson(ListMCPsResponseSchema, responseObject.data);
    return { data: resp.data?.items ?? [], total: resp.data?.total ?? 0 };
  },

  getMCP: async (id: string): Promise<MCP> => {
    const req = create(GetMCPRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/GetMCP',
      toJson(GetMCPRequestSchema, req)
    );
    const resp = fromJson(GetMCPResponseSchema, responseObject.data);
    return resp.data ?? create(MCPSchema, {});
  },

  createMCP: async (data: MCP): Promise<MCP> => {
    const req = create(CreateMCPRequestSchema, { mcp: data });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/CreateMCP',
      toJson(CreateMCPRequestSchema, req)
    );
    const resp = fromJson(CreateMCPResponseSchema, responseObject.data);
    return resp.data ?? create(MCPSchema, {});
  },

  updateMCP: async (id: string, data: MCP): Promise<MCP> => {
    const req = create(UpdateMCPRequestSchema, { id, mcp: data });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/UpdateMCP',
      toJson(UpdateMCPRequestSchema, req)
    );
    const resp = fromJson(UpdateMCPResponseSchema, responseObject.data);
    return resp.data ?? create(MCPSchema, {});
  },

  deleteMCP: async (id: string): Promise<void> => {
    const req = create(DeleteMCPRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/DeleteMCP',
      toJson(DeleteMCPRequestSchema, req)
    );
    fromJson(DeleteMCPResponseSchema, responseObject.data);
  },

  listMCPTools: async (id: string): Promise<MCPToolListResult> => {
    const req = create(ListMCPToolsRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ListMCPTools',
      toJson(ListMCPToolsRequestSchema, req)
    );
    const resp = fromJson(ListMCPToolsResponseSchema, responseObject.data);
    return { data: resp.data?.items ?? [] };
  },

  testMCPConnection: async (id: string): Promise<TestMCPConnectionData> => {
    const req = create(TestMCPConnectionRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/TestMCPConnection',
      toJson(TestMCPConnectionRequestSchema, req)
    );
    const resp = fromJson(TestMCPConnectionResponseSchema, responseObject.data);
    return resp.data ?? create(TestMCPConnectionDataSchema, {});
  },
};
